// Formatting and copy helpers shared by the Quinn components. Everything here is
// string-only (no DOM) so it runs the same during SSG and in the browser.
import type { CSSProperties } from 'react';

/** Stagger position for a [data-reveal] child. */
export const stagger = (i: number): CSSProperties => ({ ['--i' as string]: Math.min(i, 8) });

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
  '&rsquo;': '\u2019',
  '&lsquo;': '\u2018',
  '&ldquo;': '\u201C',
  '&rdquo;': '\u201D',
  '&mdash;': '\u2014',
  '&ndash;': '\u2013',
};

export function decodeEntities(s: string): string {
  return s
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&[a-z]+;|&#39;/gi, (m) => ENTITIES[m.toLowerCase()] ?? m);
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return decodeEntities(
    String(html)
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
      .replace(/<[^>]+>/g, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();
}

/** MultiHub stores "no value" as '', 'false' or 'null' on many scalar fields. */
export function clean(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === 'false' || s === 'null' || s === 'undefined') return null;
  return s;
}

export function num(v: unknown): number | null {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? null : n;
}

export const fmtN = (n: number | null | undefined) => (n == null ? '' : n.toLocaleString('en-US'));
export const money = (n: number | null | undefined) =>
  n == null ? null : '$' + Math.round(n).toLocaleString('en-US');

export function range(a: number | null, b: number | null, f: (n: number) => string = fmtN): string {
  if (a == null) return '';
  return b == null || b === a ? f(a) : `${f(a)} \u2013 ${f(b)}`;
}

/** "3 - 4" → "3 – 4", "3 - 3" → "3" */
export function rangeText(v: unknown, f: (n: number) => string = (n) => String(n)): string {
  const [a, b] = String(v ?? '').split(/\s*-\s*/).map(num);
  return range(a ?? null, b ?? null, f);
}

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
export const word = (n: number) => (n >= 0 && n <= 20 ? WORDS[n] : String(n));
export const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** "THE JARVIS" → "The Jarvis"; mixed-case names pass through untouched. */
export function titleCase(s: string | null | undefined): string {
  const t = String(s ?? '').trim();
  if (!t || t !== t.toUpperCase() || !/[A-Z]/.test(t)) return t;
  return t.toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (_, p, c) => p + c.toUpperCase());
}

/** Headlines carry no trailing period. */
export const noPeriod = (s: string) => s.replace(/\.\s*$/, '');

export const STATES: Record<string, string> = {
  AL: 'Alabama', AZ: 'Arizona', CA: 'California', CO: 'Colorado', FL: 'Florida', GA: 'Georgia', NC: 'North Carolina',
  SC: 'South Carolina', TN: 'Tennessee', TX: 'Texas', VA: 'Virginia', OH: 'Ohio', IN: 'Indiana', KY: 'Kentucky',
};

/** "Durham - a Quinn Residence" → "Durham"; "Quinn Residences at Dorchester Place" → "Dorchester Place" */
export const shortName = (name: string | null | undefined) =>
  String(name ?? '').replace(/\s*-\s*a Quinn Residence$/i, '').replace(/^Quinn Residences at\s+/i, '');

/** Paragraph inner HTML of a blurb, in order. */
export function paragraphs(html: string | null | undefined): string[] {
  const h = String(html ?? '').replace(/<br\s*\/?>/gi, ' ');
  const ps = [...h.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((m) => m[1].trim()).filter(Boolean);
  return ps.length ? ps : h.trim() ? [h.trim()] : [];
}

export interface BlurbParts {
  title: string;
  body: string;
}

/**
 * MultiHub blurbs open with a bold sentence: that sentence is the heading, the rest the lead.
 * A bold clause that continues the sentence ("<b>Residents praise X</b>, from …") stays in the lead.
 * Without bold, the first sentence is the heading.
 */
export function splitBlurb(html: string | null | undefined): BlurbParts {
  const h = String(html ?? '');
  const m = /<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/i.exec(h);
  if (m) {
    const title = stripHtml(m[2]);
    const after = h.slice(m.index + m[0].length).replace(/^(\s|<[^>]+>)+/, '');
    if (/^[,;:a-z]/.test(after)) return { title, body: stripHtml(h.replace(/<\/?(strong|b)\b[^>]*>/gi, '')) };
    return { title, body: stripHtml(after) };
  }
  const text = stripHtml(h);
  const i = text.indexOf('. ');
  return i > 0 ? { title: text.slice(0, i + 1), body: text.slice(i + 2) } : { title: text, body: '' };
}

/**
 * A heading / lead token bound to a blurb field: while it still carries the field's value it
 * shows that part of the blurb; once an editor overrides it, the override shows as written.
 */
export function blurbPart(
  value: string | null | undefined,
  fieldValue: unknown,
  part: 'title' | 'body',
  opts: { firstParagraph?: boolean } = {},
): string {
  const v = String(value ?? '');
  if (!v) return '';
  const fromField = typeof fieldValue === 'string' && v === fieldValue;
  if (!fromField && !/<[a-z][\s\S]*>/i.test(v)) return v;
  const source = opts.firstParagraph ? paragraphs(v)[0] ?? v : v;
  const parts = splitBlurb(source);
  return part === 'title' ? noPeriod(parts.title) : parts.body;
}

/** Last sentence end within `maxWords`, else the first within `hardMax`; -1 when none. */
export function sentenceCut(t: string, maxWords: number, hardMax: number): number {
  const words = (x: string) => x.trim().split(/\s+/).filter(Boolean).length;
  const ends: number[] = [];
  const re = /[.!?](?=\s|[A-Z])/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t))) ends.push(m.index + 1);
  return ends.filter((i) => words(t.slice(0, i)) <= maxWords).pop() ?? ends.find((i) => words(t.slice(0, i)) <= hardMax) ?? -1;
}

const WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAYS: Record<string, string> = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
const t12 = (t: string) => {
  const [h, m] = (t || '').split(':').map(Number);
  if (Number.isNaN(h)) return t;
  return `${((h + 11) % 12) + 1}${m ? ':' + String(m).padStart(2, '0') : ''} ${h >= 12 ? 'pm' : 'am'}`;
};

function dayRange(days: string[]): string {
  const first = DAYS[days[0]] || days[0];
  return days.length > 1 ? `${first} \u2013 ${DAYS[days[days.length - 1]] || ''}` : first;
}

export interface OfficeHour {
  day?: string;
  open_time?: string;
  close_time?: string;
}

/** Consecutive days with the same hours collapse into one row: "Mon – Fri · 8 am – 5 pm". Missing days show as Closed, in week order. */
export function hoursRows(hours: OfficeHour[], selfTour: boolean): Array<[string, string]> {
  if (!hours.length) return selfTour ? [['Sat \u2013 Sun', 'Self-guided tours']] : [];
  const byDay = new Map(hours.map((h) => [h.day || '', `${t12(h.open_time || '')} \u2013 ${t12(h.close_time || '')}`]));
  const weekendOpen = hours.some((h) => /SAT|SUN/.test(h.day || ''));
  const label = (d: string) => {
    const open = byDay.get(d);
    if (open) return open;
    if (selfTour && !weekendOpen && (d === 'SAT' || d === 'SUN')) return 'Self-guided tours';
    return 'Closed';
  };
  const runs: Array<{ days: string[]; text: string }> = [];
  for (const d of WEEK) {
    const text = label(d);
    const last = runs[runs.length - 1];
    if (last && last.text === text) last.days.push(d);
    else runs.push({ days: [d], text });
  }
  return runs.map((r) => [dayRange(r.days), r.text]);
}

export const telHref = (phone: string | null | undefined) =>
  phone ? `tel:${String(phone).replace(/[^\d+]/g, '')}` : undefined;

/** "5234567890", "+1 523.456.7890" → "(523) 456-7890"; numbers that aren't 10-digit NANP stay as entered. */
export function formatPhone(phone: string | null | undefined): string {
  const raw = String(phone ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (ten.length !== 10 || /[a-z]/i.test(raw)) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

/** Gallery photos with a usable source: many items carry only `original_url`, not `image`. */
export function galleryPhotos<T extends GalleryPhoto>(list: unknown): Array<T & { image: string }> {
  return ((list as T[]) || [])
    .map((g) => ({ ...g, image: clean(g.image) || clean(g.original_url) || '' }))
    .filter((g) => g.image);
}

export const escapeHtml = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Plain text as a richtext default; undefined when empty so the schema default applies. */
export const toParagraph = (t: string | null | undefined) => (t ? `<p>${escapeHtml(t)}</p>` : undefined);

/** The address as a Google Maps link, for a richtext default; undefined without both. */
export const addressLink = (address: string | null | undefined, url: string | null | undefined) =>
  address && url ? `<p><a href="${escapeHtml(url).replace(/"/g, '&quot;')}" target="_blank" rel="noopener">${escapeHtml(address)}</a></p>` : undefined;

/** Embedded property field for a richtext default; the editor shows it as a field chip. */
export const fieldToken = (field: string) => `<span data-type="field-token" data-field="${field}"></span>`;

/**
 * Richtext default for a heading or lead drawn from a MultiHub blurb: its bold first sentence
 * (no trailing period) or the text after it. Overrides on the token still win.
 */
export function blurbDefault(fieldValue: unknown, part: 'title' | 'body', opts: { paragraph?: number } = {}): string | undefined {
  const html = typeof fieldValue === 'string' ? fieldValue : '';
  if (!html) return undefined;
  if (opts.paragraph != null) {
    const para = paragraphs(html)[opts.paragraph];
    if (!para) return undefined;
    if (opts.paragraph > 0) return toParagraph(stripHtml(para));
    const b = splitBlurb(para);
    return toParagraph(part === 'title' ? noPeriod(b.title) : b.body);
  }
  const b = splitBlurb(html);
  return toParagraph(part === 'title' ? noPeriod(b.title) : b.body);
}
